/**
 * The panel's memory region: what this conversation is remembered for.
 *
 * Plan: docs/plans/episode-agent-parity.md S9; layout in
 * docs/backend/episodes.md ("[Target] The context panel").
 *
 * Scoped to the episode. Without `source_episode_id` the read would hand back
 * the user's whole memory store, which is a different thing wearing the same
 * label — "what this chat produced" is the question the region answers.
 *
 * S7 found no way for a guest engine to declare a memory, so what shows here
 * is what extraction produced. That is honest rather than empty, and the gap
 * is written down in docs/backend/episodes.md.
 */
import { useEffect, useState, type JSX } from "react";
import type { AppRuntime } from "../../runtime/contracts/runtime";
import { PanelRegion } from "./PanelRegion";

export interface EpisodeMemory {
  readonly id: string;
  readonly memory_type: string;
  readonly title: string;
  readonly confidence: number;
}

export function MemoryRegion({
  runtime,
  episodeId,
}: {
  readonly runtime: AppRuntime;
  readonly episodeId: string | null;
}): JSX.Element | null {
  const [memories, setMemories] = useState<readonly EpisodeMemory[]>([]);
  useEffect(() => {
    if (episodeId === null) {
      setMemories([]);
      return;
    }
    let cancelled = false;
    runtime.transport
      .rpc<readonly EpisodeMemory[]>("memory.list", { source_episode_id: episodeId })
      .then((rows): void => {
        if (!cancelled) setMemories(rows);
      })
      .catch((error: unknown) => {
        console.warn("episode memory read failed:", error);
      });
    return (): void => {
      cancelled = true;
    };
  }, [runtime, episodeId]);

  if (memories.length === 0) return null;

  return (
    <PanelRegion
      title="Memory"
      iconName="brain"
      count={String(memories.length)}
      defaultCollapsed
      testId="memory"
    >
      {memories.map((memory) => (
        <div key={memory.id} className="py-1.5">
          <div className="flex items-baseline gap-2">
            <span className="shrink-0 text-xs text-[var(--color-agent-center-text-muted)]">
              {memory.memory_type}
            </span>
            <span className="truncate text-[13px] text-[var(--color-agent-center-text)]">
              {memory.title}
            </span>
          </div>
          <div className="text-right text-xs text-[var(--color-agent-center-text-muted)]">
            confidence {memory.confidence}
          </div>
        </div>
      ))}
    </PanelRegion>
  );
}
